import { useEffect } from "react";
import { NavLink, useMatch, useNavigate } from "react-router-dom";
import { useStore } from "../store";

const PROJECT_NAV_ITEMS = [
  { to: "", label: "Overview", icon: "grid" },
  { to: "/scan", label: "Scan & Upload", icon: "upload" },
  { to: "/training", label: "Training", icon: "cpu" },
  { to: "/deploy", label: "Deployment", icon: "send" },
] as const;

const MANIFOLD_NAV_ITEMS = [
  { to: "/intent", label: "Intent Plans", icon: "brain" },
  { to: "/edge", label: "Edge Reflex", icon: "bolt" },
  { to: "/rules", label: "Restrictions", icon: "shield" },
  { to: "/egress", label: "Egress Ledger", icon: "eye" },
  { to: "/compliance", label: "Compliance Ledger", icon: "shield" },
] as const;

const ICON_MAP: Record<string, string> = {
  grid: "▦",
  upload: "⇧",
  cpu: "⚙",
  send: "➤",
  brain: "◈",
  bolt: "⚡",
  eye: "◎",
  shield: "⚖",
};

export default function Sidebar() {
  // Sidebar renders outside the <Routes> tree, so useParams() is always
  // empty here — match the location directly to find the active project.
  const matchDeep = useMatch("/projects/:id/*");
  const matchExact = useMatch("/projects/:id");
  const projectId = matchDeep?.params.id ?? matchExact?.params.id;
  const projects = useStore((s) => s.projects);
  const currentProject = useStore((s) => s.currentProject);
  // Deep links (or a refresh) land before any page calls fetchProject, so
  // resolve the active project from the sidebar's own project list too.
  const activeProject =
    currentProject?.id === projectId
      ? currentProject
      : projects.find((p) => p.id === projectId);
  const currentUser = useStore((s) => s.currentUser);
  const fetchProjects = useStore((s) => s.fetchProjects);
  const logout = useStore((s) => s.logout);
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser && projects.length === 0) {
      fetchProjects();
    }
  }, [currentUser, projects.length, fetchProjects]);

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-logo">
        Manifold <span>Kinetics</span>
      </div>
      <div className="sidebar-sublogo">
        Powered by Robot<span>wins</span>
      </div>

      {/* Main navigation */}
      <div className="sidebar-section-title">Navigation</div>
      <ul className="sidebar-nav">
        <li>
          <NavLink to="/dashboard" end className={({ isActive }) => (isActive ? "active" : "")}>
            {ICON_MAP.grid} Dashboard
          </NavLink>
        </li>
      </ul>

      {/* Project navigation (shown when inside a project) */}
      {projectId && (
        <>
          <div className="sidebar-section-title">
            Project: {activeProject?.name ?? "…"}
          </div>
          <ul className="sidebar-nav">
            {PROJECT_NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={`/projects/${projectId}${item.to}`}
                  end={item.to === ""}
                  className={({ isActive }) => (isActive ? "active" : "")}
                >
                  {ICON_MAP[item.icon]} {item.label}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="sidebar-section-title">Manifold Kinetics</div>
          <ul className="sidebar-nav">
            {MANIFOLD_NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={`/projects/${projectId}${item.to}`}
                  className={({ isActive }) => (isActive ? "active" : "")}
                >
                  {ICON_MAP[item.icon]} {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Project list */}
      <div className="sidebar-section-title">Projects</div>
      <ul className="sidebar-project-list">
        {projects.map((project) => (
          <li key={project.id}>
            <NavLink
              to={`/projects/${project.id}`}
              className={({ isActive }) =>
                `sidebar-project-item${isActive ? " active" : ""}`
              }
            >
              {project.name}
            </NavLink>
          </li>
        ))}
        {projects.length === 0 && (
          <li className="sidebar-project-item text-muted">No projects yet</li>
        )}
      </ul>

      {/* Account / logout */}
      {currentUser && (
        <div className="sidebar-account">
          <div className="sidebar-account-info" title={currentUser.email}>
            <div className="sidebar-account-name">
              {currentUser.full_name ?? currentUser.email}
            </div>
            <div className="sidebar-account-email">{currentUser.email}</div>
          </div>
          <button
            type="button"
            className="sidebar-logout"
            onClick={handleLogout}
          >
            Sign out
          </button>
        </div>
      )}
    </aside>
  );
}
